import React, { useState, useEffect } from "react";
import { Form, Button, Card, Alert } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import BasicInformation from "../components/form/BasicInformation";
import ContactInformation from "../components/form/ContactInformation";
import AddressInformation from "../components/form/AddressInformation";
import EducationInformation from "../components/form/EducationInformation";
import styled from "styled-components";
import { createApplication } from "../api/services/ApplicationService";
import { useNavigate } from "react-router-dom";

const StyledCard = styled(Card)`
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StyledCardHeader = styled(Card.Header)`
  background: linear-gradient(135deg, #4b6cb7 0%, #182848 100%);
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 1.25rem 1.5rem;
`;

const StyledForm = styled(Form)`
  .section-title {
    color: #4b6cb7;
    font-weight: 600;
    margin: 1.5rem 0 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f2f5;
    position: relative;

    &:after {
      content: "";
      position: absolute;
      left: 0;
      bottom: -2px;
      width: 60px;
      height: 2px;
      background: linear-gradient(90deg, #4b6cb7 0%, #182848 100%);
    }
  }

  .declaration-checkbox {
    margin: 1.5rem 0;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #4b6cb7;

    .form-check-input {
      margin-top: 0.25rem;
    }

    .form-check-label {
      font-weight: 500;
    }
  }
`;

const StyledButton = styled(Button)`
  min-width: 120px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;

  &.btn-outline-secondary {
    &:hover {
      background-color: #f8f9fa;
    }
  }
`;

const StudentForm = ({ onSubmit, initialData = {} }) => {
  // Yup schema: only required + basic format checks on frontend
  const schema = yup.object().shape({
    firstName: yup.string().required("First name is required").max(100),
    lastName: yup.string().required("Last name is required").max(100),
    dob: yup.date()
      .transform((v, o) => (o === "" ? null : v))
      .required("Date of birth is required")
      .max(new Date(), "Date of birth cannot be in the future"),
    gender: yup.string().required("Gender is required"),
    email: yup.string().required("Email is required").email("Must be a valid email address"),
    contact: yup.string().required("Contact number is required").matches(/^\d{10}$/, "Contact number must be 10 digits"),
    address: yup.string().required("Address is required"),
    city: yup.string().required("City is required"),
    district: yup.string().required("District is required"),
    pinCode: yup.string().required("Pin code is required").matches(/^\d{6}$/, "Pin code must be 6 digits"),
    level: yup.string().required("Level is required"),
    // nested objects — keep required checks conditional using when
    schoolDetails: yup.object().shape({
      grade: yup.string().when("level", { is: "school", then: (s) => s.required("Grade is required") }),
      schoolName: yup.string().when("level", { is: "school", then: (s) => s.required("School name is required") }),
      place: yup.string().when("level", { is: "school", then: (s) => s.required("Place is required") }),
      board: yup.string().when("level", { is: "school", then: (s) => s.required("Board is required") }),
    }),
    ugDetails: yup.object().shape({
      year: yup.string().when("level", { is: "ug", then: (s) => s.required("Year is required") }),
    }),
    pgDetails: yup.object().shape({
      year: yup.string().when("level", { is: "pg", then: (s) => s.required("Year is required") }),
    }),
    individualDetails: yup.object().shape({
      occupation: yup.string().when("level", { is: "individual", then: (s) => s.required("Occupation is required") }),
    }),
    declaration: yup.boolean().oneOf([true], "You must agree to the declaration").required("Declaration is required"),
  });

  const {
    register, handleSubmit, setError, trigger, formState: { errors, isSubmitting }, reset, watch, setValue
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialData,
      schoolDetails: { ...(initialData.schoolDetails || {}), grade: initialData?.schoolDetails?.grade || "" },
      ugDetails: { ...(initialData.ugDetails || {}) },
      pgDetails: { ...(initialData.pgDetails || {}) },
      individualDetails: { ...(initialData.individualDetails || {}) },
      examSelections: initialData?.examSelections || [],
      totalAmount: initialData?.totalAmount || 0,
      declaration: false,
    }
  });

  const handleProceed = async () => {
    // trigger validation
    const isValid = await trigger([
      "firstName",
      "middleName",
      "lastName",
      "dob",
      "gender",
      "email",
      "contact",
      "address",
      "city",
      "district",
      "pinCode",
    ]);

    if (isValid) {
      setShowEducation(true);
      window.scrollTo({ top: 600, behavior: "smooth" });
    }
  };

  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [shakeAddButton, setShakeAddButton] = useState(false);
  const [showEducation, setShowEducation] = useState(false);

  const navigate = useNavigate();

  const onFormSubmit = async (data) => {
    try {
      setSubmitError(null);
      setSubmitSuccess(false);

      const level = data.level;

      /*CLEAN / REMOVE NON-RELEVANT SECTION FIELDS*/

      if (level !== "school") delete data.schoolDetails;
      if (level !== "ug") delete data.ugDetails;
      if (level !== "pg") delete data.pgDetails;
      if (level !== "individual") delete data.individualDetails;

      // School does not need exam fields — remove them
      if (level === "school") {
        delete data.specialization;
        delete data.examLevel;
        delete data.examSelections;
      }

      /* SEND REQUEST */
      const response = await createApplication(data);

      const { _id: applicationId, applicationNo, totalAmount } = response.data;

      navigate("/payment", {
        state: {
          applicationId,
          applicationNo,
          totalAmount,
        },
      });

    } catch (error) {
      const resp = error?.response?.data;

      /* DUPLICATE EMAIL */
      // if (resp?.message?.toLowerCase().includes("email")) {
      //   setError("email", {
      //     type: "server",
      //     message: "Email already exists. Please use a different one.",
      //   });
      //   setSubmitError("Email already exists. Please use a different one.");
      //   window.scrollTo({ top: 0, behavior: "smooth" });
      //   return;
      // }

      /* ALREADY REGISTERED (PAYMENT SUCCESS) */
      if (error?.response?.status === 409) {
        setSubmitError(resp?.message || "You are already registered with this email.");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }


      /* BACKEND VALIDATION ERRORS */
      if (resp?.errors && typeof resp.errors === "object") {
        const level = watch("level");

        Object.entries(resp.errors).forEach(([field, message]) => {

          /* SPECIAL CASE — EXAM SELECTION REQUIRED */
          if (field === "examSelections") {
            const addMessage =
              "**Please click the 'Add' button after selecting Specialization and Exam Level**";

            setError("examSelections", {
              type: "server",
              message: addMessage,
            });

            setSubmitError(addMessage);
            return;
          }

          /* specialization & examLevel are NOT nested */
          if (["specialization", "examLevel"].includes(field)) {
            setError(field, { type: "server", message });
            return;
          }

          /* Map nested backend fields to correct frontend path */
          let path = field;
          if (level === "school") path = `schoolDetails.${field}`;
          else if (level === "ug") path = `ugDetails.${field}`;
          else if (level === "pg") path = `pgDetails.${field}`;
          else if (level === "individual") path = `individualDetails.${field}`;

          setError(path, { type: "server", message });
        });

        setSubmitError("Please correct the highlighted fields.");
        return;
      }

      /* OTHER ERRORS */
      setSubmitError(resp?.message || error.message || "Failed to submit form");
    }
  };

  useEffect(() => {
    const level = watch("level");

    if (level !== "ug") setValue("ugDetails", undefined);
    if (level !== "pg") setValue("pgDetails", undefined);
    if (level !== "school") setValue("schoolDetails", undefined);
    if (level !== "individual") setValue("individualDetails", undefined);

    // reset specialization/exam
    setValue("specialization", "");
    setValue("examLevel", "");
    setValue("examSelections", []);
  }, [watch("level")]);

  return (
    <>
      <StyledCard className="mb-5">
        <StyledCardHeader as="h5">Student Registration</StyledCardHeader>
        <Card.Body>
          {submitError && <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>{submitError}</Alert>}
          {submitSuccess && <Alert variant="success" onClose={() => setSubmitSuccess(false)} dismissible>Student saved successfully!</Alert>}

          <StyledForm onSubmit={handleSubmit(onFormSubmit)}>
            <BasicInformation register={register} errors={errors} />
            <ContactInformation register={register} errors={errors} />
            <AddressInformation register={register} errors={errors} />

            <div className="d-flex justify-content-end mt-3">
              <StyledButton
                type="button"
                variant="primary"
                onClick={handleProceed}
              >
                Proceed
              </StyledButton>
            </div>

            {showEducation && (
              <>
                <EducationInformation
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  shakeAddButton={shakeAddButton}
                />

                <div className="declaration-checkbox">
                  <Form.Check type="checkbox" id="declaration" label="I hereby declare that the information provided above is true and accurate." {...register("declaration")} isInvalid={!!errors.declaration} />
                  {errors.declaration && <Form.Control.Feedback type="invalid" className="d-block">{errors.declaration.message}</Form.Control.Feedback>}
                </div>

                <div className="d-flex justify-content-end mt-4 gap-3">
                  <StyledButton variant="outline-secondary" type="button" onClick={() => reset()} disabled={isSubmitting}>
                    <i className="bi bi-arrow-counterclockwise me-2" />Reset
                  </StyledButton>

                  <StyledButton variant="primary" type="submit" disabled={isSubmitting || !showEducation}>
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Processing...
                      </>
                    ) : (
                      <>
                        <i className={`bi ${initialData._id ? "bi-pencil" : "bi-save"} me-2`} />{initialData._id ? "Update" : "Submit"}
                      </>
                    )}
                  </StyledButton>
                </div>
              </>
            )}
          </StyledForm>
        </Card.Body>
      </StyledCard>
    </>
  );
};

export default StudentForm;
